/**
 * KDS Clear Page
 * Quick admin tool to clear all orders from KDS displays
 * Access at: /kds/clear
 */
import { useState } from 'react';
import { Trash2, RefreshCw, CheckCircle, AlertTriangle, Coffee, ChefHat, GlassWater, Cake } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function KDSClearPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [cleared, setCleared] = useState(false);

  // Check current KDS status
  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/kds/clear-all-status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error('Failed to check KDS status');
    } finally {
      setLoading(false);
    }
  };

  // Clear all KDS orders
  const clearAllOrders = async () => {
    if (!window.confirm('Are you sure you want to clear ALL orders from KDS displays? This cannot be undone.')) {
      return;
    }

    setClearing(true);
    try {
      const response = await api.post('/api/kds/clear-all');
      toast.success(response.data.message);
      setCleared(true);
      // Refresh status
      await checkStatus();
    } catch (error) {
      console.error('Error clearing KDS:', error);
      toast.error('Failed to clear KDS orders');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">KDS Clear Tool</h1>
          <p className="text-gray-400">Clear all orders from Kitchen Display System</p>
        </div>

        {/* Status Card */}
        <div className="bg-gray-800 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Current KDS Status</h2>
            <button
              onClick={checkStatus}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Check Status
            </button>
          </div>

          {status ? (
            <div className="space-y-4">
              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-xl">
                <span className="text-lg">Total Orders on KDS</span>
                <span className={`text-2xl font-bold ${status.total_orders_on_kds > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                  {status.total_orders_on_kds}
                </span>
              </div>

              {/* By Station */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-gray-700 rounded-xl">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Coffee className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Barista</div>
                    <div className="text-xl font-bold">{status.by_station?.barista || 0}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-700 rounded-xl">
                  <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                    <ChefHat className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Kitchen</div>
                    <div className="text-xl font-bold">{status.by_station?.kitchen || 0}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-700 rounded-xl">
                  <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
                    <GlassWater className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Juicer</div>
                    <div className="text-xl font-bold">{status.by_station?.juicer || 0}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-700 rounded-xl">
                  <div className="w-10 h-10 bg-pink-600 rounded-lg flex items-center justify-center">
                    <Cake className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Counter</div>
                    <div className="text-xl font-bold">{status.by_station?.counter || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Click &quot;Check Status&quot; to see current KDS orders</p>
            </div>
          )}
        </div>

        {/* Clear Button */}
        <div className="bg-gray-800 rounded-2xl p-6">
          {cleared ? (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-green-400 mb-2">KDS Cleared!</h3>
              <p className="text-gray-400">All displays are now empty and ready for new orders.</p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-4 mb-6">
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-yellow-400 mb-1">Warning</h3>
                  <p className="text-gray-400 text-sm">
                    This will mark ALL orders as completed and remove them from all KDS displays 
                    (Barista, Kitchen, Juicer, Counter). Use this for end-of-day cleanup or to 
                    start fresh.
                  </p>
                </div>
              </div>

              <button
                onClick={clearAllOrders}
                disabled={clearing}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-red-600 hover:bg-red-700 rounded-xl text-lg font-semibold transition-colors disabled:opacity-50"
              >
                {clearing ? (
                  <>
                    <RefreshCw className="w-6 h-6 animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-6 h-6" />
                    Clear All KDS Orders
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* Quick Links */}
        <div className="mt-8 text-center">
          <p className="text-gray-400 mb-4">KDS Display Links:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="/kds/barista" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
              Barista
            </a>
            <a href="/kds/kitchen" className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors">
              Kitchen
            </a>
            <a href="/kds/juicer" className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors">
              Juicer
            </a>
            <a href="/kds/counter" className="px-4 py-2 bg-pink-600 hover:bg-pink-700 rounded-lg transition-colors">
              Counter
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
